import { __rest } from "tslib";
import React from 'react';
import { Box, Grid, InputAdornment, makeStyles } from '@material-ui/core';
import { TextField as FormikMuiTextField } from 'formik-material-ui';
import { useField, Field } from 'formik';
import clsx from 'clsx';
import { ChevronDownIcon } from '@getgo/chameleon-icons/react';
import { reactStyling, tokens } from '@getgo/chameleon-core';
import { HelperText, MenuItem, Typography } from './../../';
const useStyles = makeStyles((theme) => {
    const { select, selectSmall, selectMedium, selectLarge, selectXlarge, selectDefaultHover } = reactStyling(theme.tokens);
    return {
        field: {
            maxWidth: 320,
            width: '100%',
        },
        fullWidth: {
            maxWidth: 'unset',
        },
        minWidth: {
            minWidth: 240,
        },
        adornment: {
            pointerEvents: 'none',
            position: 'absolute',
            fontSize: 16,
            marginLeft: 12,
            marginRight: 0,
            color: tokens.icon03,
        },
        input: Object.assign({ overflow: 'hidden', '&:hover:not(.Mui-disabled):not($focused)': Object.assign({}, selectDefaultHover), '&$focused': {
                boxShadow: tokens.highlightSmall,
                border: tokens.inverseInteractive01,
            }, '&.Mui-disabled': {
                color: tokens.text03,
                borderColor: tokens.disabled04,
            } }, select),
        invalid: {
            borderColor: tokens.danger01,
            '&:hover': {
                borderColor: `${tokens.focusError} !important`,
            },
            '&$focused': {
                boxShadow: tokens.highlightErrorSmall,
                borderColor: tokens.danger01,
            },
        },
        focused: {},
        small: Object.assign(Object.assign({}, selectSmall), { lineHeight: '30px' }),
        medium: Object.assign(Object.assign({}, selectMedium), { lineHeight: '36px' }),
        large: Object.assign(Object.assign({}, selectLarge), { lineHeight: '42px' }),
        xlarge: Object.assign(Object.assign({}, selectXlarge), { lineHeight: '48px' }),
    };
});
const useChevronStyles = makeStyles((theme) => ({
    selectArrow: {
        position: 'absolute',
        right: theme.spacing(2),
        transform: 'translate(0px, 1px)',
        pointerEvents: 'none',
        fontSize: '1.375rem!important',
        color: tokens.text02,
    },
    disabled: {
        color: tokens.text03,
    },
}));
const useSelectStyles = makeStyles(() => ({
    root: {
        backgroundColor: tokens.ui01,
        paddingLeft: ({ startAdornment }) => (startAdornment ? 40 : 12),
        paddingRight: '36px !important',
        '&.Mui-disabled': {
            backgroundColor: tokens.uiBase01,
        },
    },
}));
const SelectPlaceholder = (_a) => {
    var { children } = _a, otherProps = __rest(_a, ["children"]);
    return (React.createElement(MenuItem, Object.assign({ disabled: true, style: { display: 'none' } }, otherProps), children));
};
export const FormikSelect = (_a) => {
    var { disabled, helperText, label, name, fullWidth, inputProps, children, InputProps, placeholder, InputLabelProps, startAdornment, endAdornment, horizontal, noMargin, margin = 'normal', size = 'medium', SelectProps, contentBefore, contentAfter } = _a, otherProps = __rest(_a, ["disabled", "helperText", "label", "name", "fullWidth", "inputProps", "children", "InputProps", "placeholder", "InputLabelProps", "startAdornment", "endAdornment", "horizontal", "noMargin", "margin", "size", "SelectProps", "contentBefore", "contentAfter"]);
    const classes = useStyles({ size });
    const chevronClasses = useChevronStyles();
    const selectClasses = useSelectStyles({ startAdornment });
    const [, meta] = useField(name);
    const SelectLabel = () => !horizontal ? (React.createElement(Typography, { variant: "caption-default-02", component: "label", htmlFor: `select-input-${name}` }, label)) : (React.createElement(Typography, { variant: "body-small", component: "label", htmlFor: `select-input-${name}` }, label));
    const SelectHelperText = () => (React.createElement(Box, { mt: horizontal ? 0 : 1 },
        React.createElement(HelperText, { id: `select-${name}-helper-text` }, helperText)));
    return (React.createElement(Box, { mb: noMargin || margin === 'none' ? 0 : margin === 'dense' ? 4 : 6, width: "100%" },
        React.createElement(Grid, { container: true, wrap: "nowrap", justifyContent: horizontal ? 'space-between' : 'flex-start', alignItems: horizontal ? 'center' : undefined, direction: horizontal ? 'row' : 'column' },
            React.createElement(Grid, { container: true, item: true, direction: "column", style: { width: 'unset' } },
                label && (React.createElement(Grid, { item: true },
                    React.createElement(Box, { mb: 1 },
                        React.createElement(SelectLabel, null)))),
                helperText && horizontal && (React.createElement(Grid, { item: true },
                    React.createElement(SelectHelperText, null))),
                !horizontal && (React.createElement(Grid, { item: true, style: { maxWidth: fullWidth ? '100%' : 'unset' } },
                    React.createElement(Field, Object.assign({ id: `select-${name}`, component: FormikMuiTextField, name: name, select: true, SelectProps: Object.assign({ classes: Object.assign({}, selectClasses), labelId: `select-input-${name}`, className: clsx(Object.values(meta.error || {}).length > 1 && meta.touched && classes.invalid, classes[size]), IconComponent: () => (React.createElement("span", { className: clsx(chevronClasses.selectArrow, disabled && chevronClasses.disabled) },
                                React.createElement(ChevronDownIcon, null))) }, SelectProps), InputLabelProps: Object.assign({ shrink: true }, InputLabelProps), fullWidth: fullWidth, rows: 3, className: clsx(classes.field, fullWidth && classes.fullWidth, horizontal && classes.minWidth), disabled: disabled, InputProps: Object.assign({ classes: {
                                root: classes.input,
                                focused: classes.focused,
                                fullWidth: classes.fullWidth,
                            }, disableUnderline: true, autoComplete: 'disabled', startAdornment: startAdornment && (React.createElement(InputAdornment, { disableTypography: true, className: classes.adornment, position: "start" }, startAdornment)), endAdornment: endAdornment && (React.createElement(InputAdornment, { disableTypography: true, className: classes.adornment, position: "end" }, endAdornment)) }, InputProps), inputProps: Object.assign({ id: `select-input-${name}`, 'aria-describedby': `select-${name}-helper-text`, 'data-lpignore': true, 'aria-invalid': true }, inputProps), defaultValue: placeholder ? 'placeholder' : undefined }, otherProps),
                        placeholder && React.createElement(SelectPlaceholder, { value: "placeholder" }, placeholder),
                        children)))),
            !horizontal ? (helperText && (React.createElement(Grid, { item: true },
                React.createElement(SelectHelperText, null)))) : (React.createElement(Grid, { style: { width: 'auto' }, container: true, item: true, wrap: "nowrap", spacing: 2, alignItems: "center", justifyContent: "flex-end" },
                contentBefore && React.createElement(Grid, { item: true }, contentBefore),
                React.createElement(Grid, { item: true },
                    React.createElement(Box, { ml: contentBefore ? 0 : 4 },
                        React.createElement(Field, Object.assign({ id: `select-${name}`, component: FormikMuiTextField, name: name, select: true, SelectProps: Object.assign({ classes: Object.assign({}, selectClasses), labelId: `select-input-${name}`, className: clsx(Object.values(meta.error || {}).length > 1 && meta.touched && classes.invalid, classes[size]), IconComponent: () => (React.createElement("span", { className: clsx(chevronClasses.selectArrow, disabled && chevronClasses.disabled) },
                                    React.createElement(ChevronDownIcon, null))) }, SelectProps), InputLabelProps: Object.assign({ shrink: true }, InputLabelProps), fullWidth: fullWidth, rows: 3, className: clsx(classes.field, fullWidth && classes.fullWidth, horizontal && classes.minWidth), disabled: disabled, InputProps: Object.assign({ classes: {
                                    root: classes.input,
                                    focused: classes.focused,
                                    fullWidth: classes.fullWidth,
                                }, disableUnderline: true, autoComplete: 'disabled', startAdornment: startAdornment && (React.createElement(InputAdornment, { disableTypography: true, className: classes.adornment, position: "start" }, startAdornment)), endAdornment: endAdornment && (React.createElement(InputAdornment, { disableTypography: true, className: classes.adornment, position: "end" }, endAdornment)) }, InputProps), inputProps: Object.assign({ id: `select-input-${name}`, 'aria-describedby': `select-${name}-helper-text`, 'data-lpignore': true, 'aria-invalid': true }, inputProps), defaultValue: placeholder ? 'placeholder' : undefined }, otherProps),
                            placeholder && React.createElement(SelectPlaceholder, { value: "placeholder" }, placeholder),
                            children))),
                contentAfter && React.createElement(Grid, { item: true }, contentAfter))))));
};
